<template>
  <div class="favorite-coins mx-10">
    <div class="dropdown">
      <div class="dropdown-header flex items-center justify-between">
        <div class="header-controls flex items-center">
          <div class="select-form mr-2" @change="handleChange">
            <select v-model="periods" class="select-custom font-bold bg-white">
              <option value="">Select time</option>
              <option value="1m">1m</option>
              <option value="3m">3m</option>
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="30m">30m</option>
              <option value="1h">1h</option>
              <option value="2h">2h</option>
              <option value="4h">4h</option>
              <option value="6h">6h</option>
              <option value="12h">12h</option>
              <option value="1d">1d</option>
            </select>
          </div>
          <div class="select-form mr-2" v-if="selectedMetrics.includes('inflow30d')">
            <select v-model="inflowDays" class="select-custom font-bold bg-white">
              <option :value="15">15d</option>
              <option :value="30">30d</option>
              <option :value="45">45d</option>
              <option :value="60">60d</option>
            </select>
          </div>
          <div class="metrics-dropdown relative mr-2" @keydown.stop>
            <button type="button"
              class="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-bold hover:bg-gray-100"
              @click="isMetricsOpen = !isMetricsOpen" :aria-expanded="isMetricsOpen">
              {{ metricsLabel }}
              <svg viewBox="0 0 20 20" class="w-4 h-4">
                <path d="M5 7l5 5 5-5" fill="currentColor" />
              </svg>
            </button>
            <div v-show="isMetricsOpen" ref="metricsMenu"
              class="metrics-menu-container absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg"
              @mouseleave="closeOnHover && (isMetricsOpen = false)">
              <div class="metrics-items-scroll">
                <label v-for="opt in metricOptions" :key="opt.value"
                  class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer select-none">
                  <input type="checkbox" class="accent-indigo-600" :value="opt.value" v-model="metricsProxy" />
                  <span class="text-sm text-gray-800">{{ opt.label }}</span>
                </label>
              </div>

              <div class="metrics-buttons-footer flex justify-end gap-2 px-2 py-2 border-t border-gray-200">
                <button class="text-xs !text-gray-500 hover:text-gray-700 font-semibold" @click="selectAll">Select
                  all</button>
                <button class="text-xs !text-gray-500 hover:text-gray-700 font-semibold" @click="clearAll">Unselect
                  all</button>
              </div>
            </div>
          </div>
          <div class="multicoin-dropdown relative mr-2" @keydown.stop>
            <button type="button"
              class="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-bold hover:bg-gray-100"
              @click="isMultiCoinOpen = !isMultiCoinOpen" :aria-expanded="isMultiCoinOpen">
              {{ multiCoinLabel }}
              <svg viewBox="0 0 20 20" class="w-4 h-4">
                <path d="M5 7l5 5 5-5" fill="currentColor" />
              </svg>
            </button>
            <div v-show="isMultiCoinOpen" ref="multiCoinMenu"
              class="multicoin-menu-container absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg"
              @mouseleave="closeOnHover && (isMultiCoinOpen = false)">
              <div class="multicoin-header px-3 py-2 border-b border-gray-200 bg-gray-50">
                <span class="text-sm font-semibold !text-gray-700">Multi Coin List ({{ multiCoinCount }})</span>
              </div>
              <div class="multicoin-items-scroll">
                <div v-for="coin in multiCoinList" :key="coin.coinName" class="multicoin-item">
                  <span class="multicoin-item-name">{{ coin.coinName }}</span>
                  <span class="call-icon-small" @click="toggleIsCall(coin)">
                    <CallIcon1
                      v-if="coin.isCall === '0' || coin.isCall === 0 || coin.isCall === null || coin.isCall === undefined" />
                    <CallIcon2 v-else />
                  </span>
                </div>
                <div v-if="multiCoinList.length === 0" class="multicoin-empty">
                  No coins with isCall = 1
                </div>
              </div>
            </div>
          </div>
          <button @click="handleRefreshFavorites" class="refresh-fav-btn mr-2" :disabled="isSearching">
            MultiCoin
          </button>
          <!-- Refresh coin button -->
          <button @click="handleRefreshSelectedCoin" class="refresh-coin-btn w-[100px] px-1 py-1 "
            :disabled="!$route.params.coinName || isSearching">
            <i class="fa-solid fa-arrows-rotate text-lg"></i>
          </button>
          <button @click="toggleDropdown" class="toggle-dropdown-btn ml-2">
            <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor" :class="{ 'rotated': showDropdown }">
              <path
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </button>
          <button @click="toggleSearchFilters" class="toggle-search-btn" title="Show/Hide search filters">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"
              :class="{ 'rotated': showSearchFilters }">
              <path
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
            Block Filter Search
          </button>
        </div>
      </div>
      <div class="dropdown-content" :class="{ 'dropdown-hidden': !showDropdown }">
        <!-- Block 2: Search Filters -->
        <div class="search-filters-block" v-show="showSearchFilters">
          <div class="search-container font-bold">
            <input type="text" class="coin-search-input" v-model="searchQuery" @input="debouncedSearch"
              placeholder="Search by coin name..." />
          </div>
          <div class="exclusion-list-control relative my-1.5 flex items-center gap-2 " @keydown.stop>
            <button type="button" class="h-[38px] min-w-[190px] py-2
       !text-white text-sm font-semibold
       rounded-md
       !bg-[linear-gradient(135deg,#667eea_0%,#764ba2_100%)]
       !shadow-[0_2px_8px_rgba(102,126,234,0.3)]
       hover:!shadow-[0_4px_14px_rgba(102,126,234,0.45)]
       hover:scale-[1.02]
       hover:brightness-110
       transition-all duration-200 ease-in-out
       cursor-pointer disabled:opacity-50" @click="toggleExclusionDropdown" :aria-expanded="isExclusionDropdownOpen">
              <span>{{ exclusionSelectLabel }}</span>
              <i class="fa-solid fa-chevron-down text-xs ml-4"></i>
            </button>

            <button type="button" class="h-[38px] w-[38px] py-2
       !text-white text-sm font-semibold
       rounded-md
       !bg-[linear-gradient(135deg,#667eea_0%,#764ba2_100%)]
       !shadow-[0_2px_8px_rgba(102,126,234,0.3)]
       hover:!shadow-[0_4px_14px_rgba(102,126,234,0.45)]
       hover:scale-[1.02]
       hover:brightness-110
       transition-all duration-200 ease-in-out
       cursor-pointer disabled:opacity-50" title="Open Exclusion List" aria-label="Open Exclusion List"
              @click="openExclusionList">
              <i class="fa-solid fa-list-check"></i>
            </button>

            <div v-show="isExclusionDropdownOpen" ref="exclusionMenu"
              class="absolute left-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
              @mouseleave="isExclusionDropdownOpen = false">
              <div v-if="exclusionStore.loading && !exclusionGroups.length" class="px-3 py-4 text-sm text-gray-500">
                Loading groups...
              </div>
              <div v-else-if="!exclusionGroups.length" class="px-3 py-4 text-sm text-gray-500">
                No groups found.
              </div>
              <div v-else class="max-h-72 overflow-y-auto py-1 exclusion-list">
                <button v-for="group in exclusionGroups" :key="group.id" type="button"
                  class="flex w-full items-center gap-3 bg-white px-3 py-2 text-left text-sm hover:bg-violet-50 py-1"
                  @click="toggleExclusionGroup(group)">
                  <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded border"
                    :class="group.isExcluded ? 'border-violet-700 bg-violet-700 text-white' : 'border-gray-300 text-transparent'">
                    <i class="fa-solid fa-check text-xs"></i>
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block truncate font-semibold text-gray-800 exclusion-items">{{ group.name }}</span>
                    <span class="block truncate text-xs text-gray-500 exclusion-items">{{ group.symbolCount || 0 }}
                      symbols</span>
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div class="filters-wrapper">
            <div class="filter-block  order-block">
              <div class="filter-block-title">Mutil filter</div>
              <select class="filter-select">
                <option value="tier1">Tier 1</option>
                <option value="tier3">Tier 2</option>
                <option value="tier2">Tier 3</option>
              </select>
            </div>

            <!-- Filter Type Selection & OI Range -->
            <div class="filter-block order-block">
              <div class="filter-block-title">Primary Filter</div>
              <div class="flex flex-col gap-1.5 w-full mt-auto">
                <select v-model="filterType" class="filter-select">
                  <option value="openInterest">Open Interest</option>
                  <option value="marketCap">Market Cap</option>
                  <option value="fdv">FDV</option>
                  <option value="ratio">OI/Mc (%)</option>
                  <option value="volmc">Vol/Mc (%)</option>
                  <option value="topPositions">Top Positions</option>
                  <option value="topAccounts">Top Accounts</option>
                  <option value="accounts">Accounts</option>
                </select>

                <!-- OI Range Inputs Nested Directly Below -->
                <div v-if="filterType === 'openInterest'" class="flex items-center justify-between gap-1.5 bg-gray-50 border border-gray-300 rounded-md px-2 py-1 shadow-sm transition-all duration-300 ease-in-out">
                  <input v-model.number="minOi" type="number" placeholder="Min" class="w-[60px] h-[28px] text-center text-xs font-semibold bg-white border border-gray-300 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none text-gray-800" />
                  <span class="text-xs font-semibold text-gray-400">-</span>
                  <input v-model.number="maxOi" type="number" placeholder="Max" class="w-[60px] h-[28px] text-center text-xs font-semibold bg-white border border-gray-300 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none text-gray-800" />
                </div>
              </div>
            </div>

            <!-- Order Selection -->
            <div class="filter-block order-block">
              <div class="filter-block-title">Order</div>
              <select v-model="order" class="filter-select">
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <!-- Limit Selection -->
            <div class="filter-block limit-block">
              <div class="filter-block-title">Limit</div>
              <select v-model="limit" class="filter-select">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="0">All</option>
              </select>
            </div>

            <div class="filter-block order-block">
              <div class="filter-block-title">Coin Type</div>
              <div class="type-checkboxes">
                <label class="type-checkbox">
                  <input type="checkbox" value="spot" v-model="selectedTypes" />
                  Spot
                </label>
                <label class="type-checkbox">
                  <input type="checkbox" value="futures" v-model="selectedTypes" />
                  Futures
                </label>
                <label class="type-checkbox">
                  <input type="checkbox" value="alpha" v-model="selectedTypes" />
                  Alpha
                </label>
              </div>
            </div>



            <div class="flex flex-col gap-2 w-[120px] flex-shrink-0">
              <button @click="applySearch" :disabled="isSearching" class="w-full py-2
         !text-white text-sm font-semibold
         rounded-md
         !bg-[linear-gradient(135deg,#667eea_0%,#764ba2_100%)]
         !shadow-[0_2px_8px_rgba(102,126,234,0.3)]
         cursor-pointer disabled:opacity-50">
                <span v-if="!isSearching">Search</span>
                <span v-else class="loading-spinner"></span>
              </button>

              <button @click="clearSearch" :disabled="isSearching" class="px-6 py-2 h-[38px]
         text-white text-sm font-semibold whitespace-nowrap
         rounded-md
         bg-[linear-gradient(135deg,#adb7e4_0%,#7fbee2_100%)]
         shadow-[0_2px_8px_rgba(102,126,234,0.3)]
         transition-all duration-200
         cursor-pointer">
                Clear
              </button>
            </div>

          </div>
        </div>

        <!-- Block 3: Coins List -->
        <div class="coins-list-container">
          <div class="coins-list-summary" style="padding: 10px 15px; font-size: 14px; font-weight: bold; border-bottom: 1px solid rgba(128, 128, 128, 0.15); display: flex; align-items: center; justify-content: space-between;">
            <span>Tổng số: <strong style="color: #667eea; font-size: 16px;">{{ displayedCoins.length }}</strong> coin</span>
          </div>
          <div class="coins-list font-bold">
            <div v-for="coin in displayedCoins" :key="coin.coinName" class="coin-item"
              :class="{ 'selected': selectedCoin && selectedCoin.coinName === coin.coinName }">
              <!-- <div class=""> -->

              <router-link class="coin-name" :to="{ name: 'BinanceCoin', params: { coinName: coin.coinName } }"
                :title="'Call chart for ' + coin.coinName" @click="selectedCoin = coin">
                {{ coin.coinName }}
              </router-link>


              <!-- </div> -->

              <!-- Display metrics (only after Search) -->
              <div class="coin-metrics" v-show="isFilterActive">
                <span class="coin-metric metric-oi" :class="{ 'metric-highlight': filterType === 'openInterest' }" title="Open Interest">
                  OI: {{ coin.openInterest ? formatNumber(coin.openInterest) : '—' }}
                </span>
                <span class="coin-metric metric-mcap" :class="{ 'metric-highlight': filterType === 'marketCap' }" title="Market Cap">
                  MCap: {{ coin.marketCap ? formatNumber(coin.marketCap) : '—' }}
                </span>
                <span class="coin-metric metric-fdv" :class="{ 'metric-highlight': filterType === 'fdv' }" title="FDV">
                  FDV: {{ coin.fdv ? formatNumber(coin.fdv) : '—' }}
                </span>
                <span class="coin-metric ratio-metric metric-oimc" :class="{ 'metric-highlight': filterType === 'ratio' }" title="Ratio">
                  OI/Mc: {{ coin.ratio !== null && coin.ratio !== undefined ? formatPercent(coin.ratio) + '%' : '—' }}
                </span>
                <span class="coin-metric ratio-metric metric-volmc" :class="{ 'metric-highlight': filterType === 'volmc' }" title="Vol/Mc (%)">
                  Vol/Mc: {{ coin.volmc !== null && coin.volmc !== undefined ? formatPercent(coin.volmc) + '%' : '—' }}
                </span>
                <span class="coin-metric ls-metric metric-toppos" :class="{ 'metric-highlight': filterType === 'topPositions' }" title="Top Positions L/S Ratio">
                  Top Pos: {{ coin.topPositions !== null && coin.topPositions !== undefined && coin.topPositions !== '' ? coin.topPositions : '—' }}
                </span>
                <span class="coin-metric ls-metric metric-topacc" :class="{ 'metric-highlight': filterType === 'topAccounts' }" title="Top Accounts L/S Ratio">
                  Top Acc: {{ coin.topAccounts !== null && coin.topAccounts !== undefined && coin.topAccounts !== '' ? coin.topAccounts : '—' }}
                </span>
                <span class="coin-metric ls-metric metric-acc" :class="{ 'metric-highlight': filterType === 'accounts' }" title="Global Accounts L/S Ratio">
                  Acc: {{ coin.globalAccounts !== null && coin.globalAccounts !== undefined && coin.globalAccounts !== '' ? coin.globalAccounts : '—' }}
                </span>
              </div>

              <!-- Icons (favorite, call) - right-aligned -->
              <div class="flex items-center flex-shrink-0 gap-1 ml-auto">
                <span class="new-icon">
                    <NewIcon v-if="coin.isNew" />
                  </span>
                  <span class="star-icon" @click="toggleFavorite(coin)">
                    <StarIcon1 v-if="coin.favoriteFlag === '0' || coin.favoriteFlag === 0" />
                    <StarIcon2 v-else />
                  </span>
                  <span class="call-icon" @click="toggleIsCall(coin)">
                    <CallIcon1
                      v-if="coin.isCall === '0' || coin.isCall === 0 || coin.isCall === null || coin.isCall === undefined" />
                    <CallIcon2 v-else />
                  </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ExclusionList v-if="showExclusionList" :available-symbols="allAvailableCoins" @apply="handleExclusionApply"
      @close="showExclusionList = false" />
  </div>
</template>

<script>
import StarIcon1 from "@/assets/svg/StarIcon1.vue";
import { formatNumber, formatPercent } from "@/utils/number";
import debounce from "lodash/debounce";
import StarIcon2 from "@/assets/svg/StarIcon2.vue";
import NewIcon from "@/assets/svg/NewIcon.vue";
import CallIcon1 from "@/assets/svg/CallIcon1.vue";
import CallIcon2 from "@/assets/svg/CallIcon2.vue";
import BinanceService from "@/services/BinanceService";
import { METRIC_OPTIONS } from "@/constants/metrics";
import ExclusionList from "@/components/exclusion/ExclusionList.vue";
import { useExclusionGroupsStore } from "@/stores/exclusionGroups";

export default {
  components: {
    StarIcon1,
    StarIcon2,
    NewIcon,
    CallIcon1,
    CallIcon2,
    ExclusionList
  },
  props: {
    selectedMetrics: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const exclusionStore = useExclusionGroupsStore();
    return {
      exclusionStore,
    };
  },
  data() {
    return {
      periods: "15m",
      coins: [],
      searchQuery: "",
      filteredCoins: [],
      loading: false,
      initialMetrics: [],
      metricOptions: METRIC_OPTIONS,
      isMetricsOpen: false,
      isMultiCoinOpen: false,
      closeOnHover: true,
      // New filter params
      filterType: 'openInterest',
      order: 'desc',
      limit: 10,
      selectedTypes: ['spot', 'futures', 'alpha'],
      isSearching: false,
      selectedCoin: null,
      showDropdown: true,
      showExclusionList: false,
      allAvailableCoins: [],
      isExclusionDropdownOpen: false,
      inflowDays: 30,
      isFilterActive: false,
      minOi: null,
      maxOi: null,
    };
  },
  async created() {
    await this.fetchCoins();
  },
  computed: {
    displayedCoins() {
      const selected = new Set(this.selectedTypes);
      let typeFiltered;
      if (selected.size === 0) {
        typeFiltered = [];
      } else if (selected.size === 3) {
        // All types selected → show all coins (no type filtering)
        typeFiltered = this.filteredCoins;
      } else {
        typeFiltered = this.filteredCoins.filter(c => {
          const t = String(c.type || '').toLowerCase();
          if (selected.has('spot')) {
            if (t.includes('spot') && !t.includes('futures')) {
              return true;
            }
          }
          if (selected.has('futures')) {
            if (t.includes('futures') && !t.includes('spot') && !t.includes('alpha')) {
              return true;
            }
          }
          if (selected.has('alpha')) {
            if (t.includes('alpha')) {
              return true;
            }
          }
          return false;
        });
      }

      return [...typeFiltered].sort((a, b) => {
        if ((a.favoriteFlag === "1" || a.favoriteFlag === 1) && (b.favoriteFlag !== "1" && b.favoriteFlag !== 1)) return -1;
        if ((a.favoriteFlag !== "1" && a.favoriteFlag !== 1) && (b.favoriteFlag === "1" || b.favoriteFlag === 1)) return 1;
        return 0;
      });
    },
    metricsLabel() {
      if (!this.selectedMetrics.length) return 'Select metrics';
      if (this.selectedMetrics.length === this.metricOptions.length) return 'All metrics';
      const names = this.metricOptions
        .filter(o => this.selectedMetrics.includes(o.value))
        .map(o => o.label);
      return names.length <= 2 ? names.join(', ') : `${names.length} selected`;
    },
    multiCoinList() {
      const selected = new Set(this.selectedTypes);
      const base = this.coins.filter(coin => coin.isCall === "1" || coin.isCall === 1);
      if (selected.size === 0) return [];
      if (selected.size === 3) return base;
      return base.filter(c => {
        const t = String(c.type || '').toLowerCase();
        if (selected.has('spot')) {
          if (t.includes('spot') && !t.includes('futures')) {
            return true;
          }
        }
        if (selected.has('futures')) {
          if (t.includes('futures') && !t.includes('spot') && !t.includes('alpha')) {
            return true;
          }
        }
        if (selected.has('alpha')) {
          if (t.includes('alpha')) {
            return true;
          }
        }
        return false;
      });
    },
    multiCoinCount() {
      return this.multiCoinList.length;
    },
    multiCoinLabel() {
      return `List Multi Coin (${this.multiCoinCount})`;
    },
    exclusionGroups() {
      return [
        ...this.exclusionStore.systemGroups,
        ...this.exclusionStore.blockedGroups,
        ...this.exclusionStore.customGroups,
      ];
    },
    exclusionSelectLabel() {
      const excludedCount = this.exclusionGroups.filter(group => group.isExcluded).length;
      if (!this.exclusionGroups.length) return 'Exclusion List';
      return excludedCount ? `Exclusion List (${excludedCount})` : 'Exclusion List';
    },
    metricsProxy: {
      get() {
        return this.selectedMetrics;
      },
      set(val) {
        this.$emit("metrics-changed", val);
      }
    }

  },
  watch: {
    inflowDays(newVal) {
      this.$emit("inflow-days-changed", newVal);
    },
    filterType(newVal) {
      if (newVal !== 'openInterest') {
        this.minOi = null;
        this.maxOi = null;
      }
    }
  },
  methods: {
    formatNumber,
    formatPercent,
    async handleFetchCoins() {
      this.$emit("handle-fetchCoins");
    },
    async handleChange() {
      // Khi chọn time frame, luôn gen tất cả chart (bỏ qua kiểm tra metrics)
      this.$emit("period-selected", this.periods);
    },
    async handleRefreshFavorites() {
      // Refresh all favorite coins
      if (!this.periods) {
        this.$toast.open({
          message: 'Please select a time period first!',
          type: 'warning',
          position: 'top-right',
        });
        return;
      }

      // Clear selected coin and close search/list UI
      this.selectedCoin = null;
      this.showDropdown = false;

      this.isSearching = true;
      try {
        await this.fetchCoins(this.isFilterActive);
        // Emit event to refresh all favorites (match parent listener)
        this.$emit("refresh-clicked", this.periods);
      } finally {
        this.isSearching = false;
      }
    },
    async handleRefreshSelectedCoin() {
      // Refresh the currently selected coin
      const coinNameFromRoute = this.$route.params.coinName;

      let coin = this.selectedCoin;

      if (!coin && coinNameFromRoute) {
        coin = this.coins.find(c =>
          String(c.coinName).toLowerCase() === String(coinNameFromRoute).toLowerCase()
        );
      }
      if (!coin) {
        this.$toast.open({
          message: 'Please select a coin first by clicking "Call chart" button!',
          type: 'warning',
          position: 'top-right',
        });
        return;
      }

      if (!this.periods) {
        this.$toast.open({
          message: 'Please select a time period first!',
          type: 'warning',
          position: 'top-right',
        });
        return;
      }

      // Close search/list UI and emit event to refresh selected coin
      this.showDropdown = false;
      if (coinNameFromRoute) {
        // có coin → single coin
        this.$emit("single-coin-search", {
          coin: coin,
          period: this.periods
        });
      } else {
        // không có coin → favorites
        this.$emit("refresh-clicked", this.periods);
      }
    },
    callChartForCoin(coin) {
      // Set this coin as selected
      this.selectedCoin = coin;

      // Open a new tab with the requested URL
      this.$router.push({
        name: 'BinanceCoin',
        params: { coinName: coin.coinName }
      });
      // Close the dropdown
      // this.showDropdown = false;
    },
    async fetchCoins(withFilters = false) {
      try {
        const params = {
          excludeTagEnabled: withFilters,
          ...(withFilters
            ? {
              filterType: this.filterType,
              order: this.order,
              limit: Number(this.limit) > 0 ? Number(this.limit) : null,
              types: this.selectedTypes.join(","),
              period: this.periods,
              minOi: this.minOi ? this.minOi * 1000000 : null,
              maxOi: this.maxOi ? this.maxOi * 1000000 : null,
            }
            : {}),
        };
        const data = await BinanceService.getCoins(params);

        this.coins = data.map(coin => ({
          ...coin,
          favoriteFlag: coin.favoriteFlag || "0",
          isCall: coin.isCall || "0",
        }));
        this.filteredCoins = this.coins;
        // Phát sự kiện để báo cho cha biết đã load xong dữ liệu
        this.$emit("coins-loaded", { coins: data, fromFilter: withFilters });
        return data;
      } catch (error) {
        console.error("Error fetching coins:", error);
        this.$toast.open({
          message: "Không thể tải danh sách coin. Kiểm tra API/backend.",
          type: "error",
          position: "top-right",
        });
        return [];
      }
    },

    debouncedSearch: debounce(function () {
      const query = this.searchQuery.trim().toLowerCase();

      if (!query) {
        this.filteredCoins = this.coins;
        return;
      }

      const exactMatch = [];
      const startsWithMatch = [];
      const containsMatch = [];

      for (const coin of this.coins) {
        const name = coin.coinName.toLowerCase();
        if (name === query) {
          exactMatch.push(coin);
        } else if (name.startsWith(query)) {
          startsWithMatch.push(coin);
        } else if (name.includes(query)) {
          containsMatch.push(coin);
        }
      }

      this.filteredCoins = [...exactMatch, ...startsWithMatch, ...containsMatch];
    }, 300),

    async applySearch() {
      // Apply search with name and OI filters
      if (!this.periods) {
        this.$toast.open({
          message: 'Vui lòng chọn thời gian trước khi search!',
          type: 'warning',
          position: 'top-right',
        });
        return;
      }

      this.isSearching = true;
      try {
        const data = await this.fetchCoins(true);

        if (!data.length) {
          this.$toast.open({
            message: "Không tìm thấy coin phù hợp với bộ lọc.",
            type: "warning",
            position: "top-right",
          });
          return;
        }

        // Also apply name filter if searchQuery is not empty
        if (this.searchQuery.trim()) {
          this.filteredCoins = this.coins.filter(coin =>
            coin.coinName.toLowerCase().includes(this.searchQuery.toLowerCase())
          );
        }

        this.isFilterActive = true;
        this.showDropdown = true;

        this.$toast.open({
          message: `Tìm thấy ${this.filteredCoins.length} coin.`,
          type: "success",
          position: "top-right",
        });

        await this.$nextTick();
      } finally {
        this.isSearching = false;
      }
    },

    async toggleFavorite(coin) {
      if (this.loading) return;
      this.loading = true;

      try {
        const newFavoriteFlag = (coin.favoriteFlag === "1" || coin.favoriteFlag === 1) ? "0" : "1";
        coin.favoriteFlag = newFavoriteFlag;
        const requestBody = [{
          coinName: coin.coinName,
          favoriteFlag: coin.favoriteFlag,
          isCall: coin.isCall,
        }];

        // Call API /binance/update-favorite
        const data = await BinanceService.updateFavorite(requestBody);

        // Update locally to preserve search metrics
        const targetCoin = this.coins.find(c => c.coinName === coin.coinName);
        if (targetCoin) {
          targetCoin.favoriteFlag = newFavoriteFlag;
        }
        const targetFiltered = this.filteredCoins.find(c => c.coinName === coin.coinName);
        if (targetFiltered) {
          targetFiltered.favoriteFlag = newFavoriteFlag;
        }

        // Map the favorites flag back to the backend's updated data to notify parent correctly
        const updatedData = data.map(updatedCoin => {
          const matched = this.coins.find(c => c.coinName === updatedCoin.coinName);
          return matched ? { ...matched } : updatedCoin;
        });

        this.$emit("coins-loaded", { coins: updatedData, fromFilter: this.isFilterActive });
        this.$toast.open({
          message: `${coin.coinName} ${newFavoriteFlag === "1" ? "đã được thêm vào" : "đã xóa khỏi"} favorites!`,
          type: "success",
          position: "top-right",
        });
      } catch (error) {
        console.error("Error toggling favorite:", error);
        this.$toast.open({
          message: "Lỗi cập nhật trạng thái favorites!",
          type: "error",
          position: "top-right",
        });
      } finally {
        this.loading = false;
      }
    },

    toggleSearchFilters() {
      this.showSearchFilters = !this.showSearchFilters;
    },

    async openExclusionList() {
      const loading = this.$loading.show();
      try {
        const data = await BinanceService.getCoins({
          excludeTagEnabled: false,
          types: 'spot,futures,alpha'
        });
        this.allAvailableCoins = data || [];
        this.showExclusionList = true;
        this.isExclusionDropdownOpen = false;
      } catch (error) {
        console.error("Lỗi khi tải toàn bộ coins cho Exclusion List:", error);
      } finally {
        loading.hide();
      }
    },

    async handleExclusionApply() {
      await this.fetchCoins(false);
    },

    async toggleExclusionDropdown() {
      this.isExclusionDropdownOpen = !this.isExclusionDropdownOpen;
      if (this.isExclusionDropdownOpen && !this.exclusionGroups.length) {
        await this.exclusionStore.fetchGroups();
      }
    },

    async toggleExclusionGroup(group) {
      try {
        await this.exclusionStore.updateGroup(group.id, {
          isExcluded: !group.isExcluded,
        });
        await this.fetchCoins(false);
        this.$toast.open({
          message: 'Exclusion group updated.',
          type: 'success',
          position: 'top-right',
        });
      } catch (error) {
        console.error('Error updating exclusion group:', error);
        const errMsg = error?.response?.data?.message || error?.message || 'Unable to update exclusion group.';
        this.$toast.open({
          message: errMsg,
          type: 'error',
          position: 'top-right',
        });
      }
    },

    async clearSearch() {
      this.isSearching = true;
      try {
        this.searchQuery = "";
        this.filterType = "openInterest";
        this.order = "desc";
        this.limit = 10;
        this.selectedTypes = ['spot', 'futures', 'alpha'];
        this.filteredCoins = [];
        this.isFilterActive = false;
        this.minOi = null;
        this.maxOi = null;
        await this.fetchCoins(false);
      } finally {
        this.isSearching = false;
      }
    },

    async toggleIsCall(coin) {
      if (this.loading) return;
      this.loading = true;

      try {
        const newIsCall = (coin.isCall === "1" || coin.isCall === 1) ? "0" : "1";
        coin.isCall = newIsCall;
        const requestBody = [{
          coinName: coin.coinName,
          favoriteFlag: coin.favoriteFlag,
          isCall: coin.isCall,
        }];
        // Call API /binance/update-favorite
        const data = await BinanceService.updateFavorite(requestBody);

        // Update locally to preserve search metrics
        const targetCoin = this.coins.find(c => c.coinName === coin.coinName);
        if (targetCoin) {
          targetCoin.isCall = newIsCall;
        }
        const targetFiltered = this.filteredCoins.find(c => c.coinName === coin.coinName);
        if (targetFiltered) {
          targetFiltered.isCall = newIsCall;
        }

        // Map the call flag back to the backend's updated data to notify parent correctly
        const updatedData = data.map(updatedCoin => {
          const matched = this.coins.find(c => c.coinName === updatedCoin.coinName);
          return matched ? { ...matched } : updatedCoin;
        });

        this.$emit("coins-loaded", { coins: updatedData, fromFilter: this.isFilterActive });
        this.$toast.open({
          message: `${coin.coinName} ${newIsCall === "1" ? "đã được thêm vào" : "đã xóa khỏi"} call list!`,
          type: "success",
          position: "top-right",
        });
      } catch (error) {
        console.error("Error toggling isCall:", error);
        this.$toast.open({
          message: "Lỗi cập nhật trạng thái isCall!",
          type: "error",
          position: "top-right",
        });
      } finally {
        this.loading = false;
      }
    },
    selectAll() {
      this.metricsProxy = this.metricOptions.map(o => o.value);
    },
    clearAll() {
      this.metricsProxy = [];
    },

    // Dropdown toggle
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },

    handleClickOutside(e) {
      const exclusionMenu = this.$refs.exclusionMenu;
      const exclusionRoot = e.target.closest('.exclusion-list-control');
      if (this.isExclusionDropdownOpen && exclusionMenu && !exclusionMenu.contains(e.target) && !exclusionRoot) {
        this.isExclusionDropdownOpen = false;
      }

      if (!this.isMetricsOpen) return;
      const menu = this.$refs.metricsMenu;
      if (menu && !menu.contains(e.target)) {
        const btn = e.target.closest('.metrics-dropdown');
        if (!btn || !btn.contains(this.$el)) {
          this.isMetricsOpen = false;
        }
        if (!this.$el.contains(e.target)) this.isMetricsOpen = false;
      } else if (!this.$el.contains(e.target)) {
        this.isMetricsOpen = false;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside, true);
    this.exclusionStore.fetchGroups();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
  },
};
</script>

<style>
body.dark-mode .exclusion-items {
  color: white;
}

body.dark-mode .exclusion-list {
  background: #2b3139;
  border-color: #2b3139;
}

/* Highlighted metric (light mode) */
.metric-highlight {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  border-radius: 4px;
  padding: 2px 6px !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.35);
  animation: metric-pulse 1.5s ease-in-out infinite alternate;
}

@keyframes metric-pulse {
  0% { box-shadow: 0 2px 8px rgba(102, 126, 234, 0.35); }
  100% { box-shadow: 0 2px 12px rgba(118, 75, 162, 0.55); }
}

/* Highlighted metric (dark mode) */
body.dark-mode .metric-highlight {
  background: linear-gradient(135deg, #7c3aed 0%, #06b6d4 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 10px rgba(124, 58, 237, 0.45);
}

body.dark-mode .metric-highlight {
  animation-name: metric-pulse-dark;
}

@keyframes metric-pulse-dark {
  0% { box-shadow: 0 2px 8px rgba(124, 58, 237, 0.4); }
  100% { box-shadow: 0 2px 14px rgba(6, 182, 212, 0.5); }
}
</style>
